import styles from "./style.postcss";

import React from "react";
import { bindActionCreators } from "redux";
import { Link } from "react-router";
import { push } from "react-router-redux";
import { connect, Icon, testClass } from "omni-common-ui";
import ReactGA from "react-ga4";
import UnsavedChangesTrackingPageComponent from "components/UnsavedChangesTrackingPageComponent";
import IndividualDevelopmentPlanStatus, {
  IdpStatus as IDP_STATUS,
} from "components/IndividualDevelopmentPlanStatus";
import CourseViewTable from "components/CourseViewTable";
import ExportAllHandler from "./ExportAllHandler";
import { actions as groupActions } from "containers/Group";
import {
  actions as individualDevelopmentPlans,
  accessors as idpAccessors,
} from "data/IndividualDevelopmentPlans";
import { actions as GroupMembers } from "data/GroupMembers";
import PropTypes from "prop-types";
import { Modes } from "./routes";
import classnames from "classnames";

const COLUMN_PERIOD = "period";
const COLUMN_SKILL1 = "skill1";
const COLUMN_SKILL2 = "skill2";
const COLUMN_SKILL3 = "skill3";
const COLUMN_STATUS = "status";
const COLUMN_ID = "id";

class IndividualDevelopmentPlanListView extends UnsavedChangesTrackingPageComponent {
  routerWillLeave(nextLocation) {
    if (nextLocation && nextLocation.pathname.endsWith(Modes.GENERATE_ALL)) {
      this.props.groupIdpId &&
        this.props.generateIndividualDevelopmentList(this.props.groupIdpId);
      return false;
    }
  }

  _gaLinkClicked() {
    ReactGA.event({
      category: "Individual Development Plan",
      action: "Clicked link to Individual Development Plan detail",
    });
  }

  _getStatus(studentIdp) {
    if (
      studentIdp.shouldShowSuggestions &&
      IDP_STATUS.INCOMPLETE === studentIdp.status.toLowerCase() &&
      !studentIdp[COLUMN_SKILL1] &&
      !studentIdp[COLUMN_SKILL2] &&
      !studentIdp[COLUMN_SKILL3]
    ) {
      return "NA";
    }

    return studentIdp.status;
  }

  _renderCell(member, column) {
    const idpList = this.props.individualDevelopmentPlanList;
    if (!idpList || !idpList.size) return "-";

    const studentIdp = idpList.find(
      (idp) => idp.studentId === member.studentGeneralInfo.id,
    );

    if (!studentIdp) return column !== COLUMN_ID && "-";
    switch (column) {
      case COLUMN_STATUS:
        return (
          <IndividualDevelopmentPlanStatus
            status={this._getStatus(studentIdp)}
          />
        );
      case COLUMN_ID:
        return (
          <Link
            className={classnames(
              styles.IndividualDevelopmentPlanListViewLink,
              testClass("report-link-cell"),
            )}
            to={this.props.buildRoute(`./student/${studentIdp.studentId}`)}
            draggable={false}
            onClick={() => this._gaLinkClicked()}
          >
            REPORT{" "}
            <Icon
              id="arrow-right"
              className={styles.IndividualDevelopmentPlanListViewLink_icon}
            />
          </Link>
        );
      default:
        return studentIdp[column] || "-";
    }
  }

  _renderIdpList() {
    return (
      <CourseViewTable students={this.props.members} onlyRowDivider>
        <CourseViewTable.Header>
          <CourseViewTable.Row>
            <CourseViewTable.Cell columnId={COLUMN_PERIOD} small>
              Period covered
            </CourseViewTable.Cell>
            <CourseViewTable.Cell columnId={COLUMN_SKILL1} small>
              Skill 1
            </CourseViewTable.Cell>
            <CourseViewTable.Cell columnId={COLUMN_SKILL2} small>
              Skill 2
            </CourseViewTable.Cell>
            <CourseViewTable.Cell columnId={COLUMN_SKILL3} small>
              Skill 3
            </CourseViewTable.Cell>
            <CourseViewTable.Cell columnId={COLUMN_STATUS} small>
              Status
            </CourseViewTable.Cell>
            {this.props.individualDevelopmentPlanList &&
              this.props.individualDevelopmentPlanList.size > 0 && (
                <CourseViewTable.Cell columnId={COLUMN_ID} small />
              )}
          </CourseViewTable.Row>
        </CourseViewTable.Header>
        <CourseViewTable.Body>
          {(member, column) => this._renderCell(member, column)}
        </CourseViewTable.Body>
      </CourseViewTable>
    );
  }

  _renderExportDialog() {
    const {
      mode,
      buildRoute,
      pushRoute,
      languageCode,
      individualDevelopmentPlanList,
      groupIdpId,
      groupId,
      members,
      reportName,
    } = this.props;
    const isInExportAllRoute = mode === Modes.EXPORT_ALL;
    const memberStudentIds = members.map(
      (member) => member.studentGeneralInfo.id,
    );
    const exportableIDPList =
      individualDevelopmentPlanList &&
      individualDevelopmentPlanList.filter((idp) =>
        memberStudentIds.includes(idp.studentId),
      );
    return (
      individualDevelopmentPlanList &&
      individualDevelopmentPlanList.size > 0 &&
      isInExportAllRoute && (
        <ExportAllHandler
          individualDevelopmentPlanList={exportableIDPList}
          groupIdpId={groupIdpId}
          languageCode={languageCode}
          pushRoute={pushRoute}
          buildRoute={buildRoute}
          groupId={groupId}
          reportName={reportName}
        />
      )
    );
  }

  render() {
    return (
      <div>
        <div className={styles.IndividualDevelopmentPlanListView_content}>
          {this._renderIdpList()}
          {this._renderExportDialog()}
        </div>
      </div>
    );
  }
}

IndividualDevelopmentPlanListView.propTypes = {
  buildRoute: PropTypes.func.isRequired,
  pushRoute: PropTypes.func.isRequired,
  members: PropTypes.object.isRequired,
  groupIdpId: PropTypes.string.isRequired,
  individualDevelopmentPlanList: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
  reportName: PropTypes.string.isRequired,
};

function mapStateToProps(state, ownProps) {
  const { groupId, groupIdpId, mode } = ownProps.params;
  const { timeZone } = groupActions.getCentre(state, groupId);
  const group = state.get("rootReducer").get("groups").get(groupId).data;
  const languageCode = group.get("languageCode");

  const groupIdpList = state
    .get("rootReducer")
    .get("individualDevelopmentPlanList");
  const originIndividualDevelopmentPlanList = idpAccessors.getIdpList(
    groupIdpList,
    groupIdpId,
  );

  const individualDevelopmentPlanList = idpAccessors.formatToRenderUI(
    originIndividualDevelopmentPlanList,
    timeZone,
  );
  const members = GroupMembers.getMembersByGroup(state, groupId);

  const reportName = idpAccessors.getReportName(state, groupIdpId);
  return {
    reportName,
    mode,
    state,
    members,
    individualDevelopmentPlanList,
    groupId,
    groupIdpId,
    languageCode,
  };
}

function mapDispatchToProps(dispatch) {
  return Object.assign(
    {},
    { pushRoute: (path) => dispatch(push(path)) },
    bindActionCreators(individualDevelopmentPlans, dispatch),
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(IndividualDevelopmentPlanListView);



// WEBPACK FOOTER //
// ./containers/Group/IndividualDevelopmentPlan/IndividualDevelopmentPlanListView/component.jsx